import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  TextField,
  Button,
} from '@mui/material';
import {
  ConfirmationNumberRounded,
  CheckCircleRounded,
  HourglassTopRounded,
  PaymentsRounded,
  ArrowForwardRounded,
} from '@mui/icons-material';
import { colors } from '../../theme/theme';
import StatCard from '../../components/StatCard';
import { useQueueDashboard, DashboardPeriod } from '../../hooks/useQueueDashboard';
import { PeriodTabs } from './dashboardParts';
import { formatRupees, sumCounts, pendingCount, STATUS_META } from './dashboardConfig';

const card = { bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, borderRadius: '14px' };

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error, fetchDashboard } = useQueueDashboard();
  const [period, setPeriod] = useState<DashboardPeriod>('today');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  useEffect(() => {
    if (period !== 'custom') fetchDashboard(period);
  }, [period, fetchDashboard]);

  const total = sumCounts(data?.total_token_counts);
  const completed = data?.total_token_counts.completed ?? 0;
  const pending = pendingCount(data?.total_token_counts);

  // Status breakdown bars
  const barRows = data
    ? [
        { label: 'Completed', n: data.total_token_counts.completed, c: STATUS_META[0].color },
        { label: 'Pending', n: pending, c: colors.primary },
        { label: 'Serving', n: data.total_token_counts.in_progress, c: '#06C646' },
        { label: 'Skipped', n: data.total_token_counts.skipped, c: STATUS_META[5].color },
        { label: 'Cancelled', n: data.total_token_counts.cancelled + data.total_token_counts.no_show, c: STATUS_META[6].color },
      ]
    : [];
  const barMax = Math.max(...barRows.map((r) => r.n), 1);

  const glance = data
    ? [
        { c: colors.primary, label: 'Queues tracked', n: data.queues.length },
        { c: '#06C646', label: 'Serving now', n: data.total_token_counts.in_progress },
        { c: STATUS_META[5].color, label: 'Skipped', n: data.total_token_counts.skipped },
        { c: STATUS_META[6].color, label: 'Cancelled / no-show', n: data.total_token_counts.cancelled + data.total_token_counts.no_show },
      ]
    : [];

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.75 }}>
        <PeriodTabs value={period} onChange={setPeriod} />
        {data && <Typography sx={{ color: colors.textSecondary, fontSize: 13, alignSelf: 'center' }}>{data.period_label}</Typography>}
      </Box>

      {period === 'custom' && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
          <TextField type="date" size="small" value={start} onChange={(e) => setStart(e.target.value)} />
          <Typography sx={{ alignSelf: 'center', color: colors.textSecondary }}>to</Typography>
          <TextField type="date" size="small" value={end} onChange={(e) => setEnd(e.target.value)} />
          <Button variant="contained" disabled={!start || !end} onClick={() => fetchDashboard('custom', start, end)}>Apply</Button>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>
      ) : !data ? (
        <Alert severity="info">No dashboard data available.</Alert>
      ) : (
        <>
          {/* KPI cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4,1fr)' }, gap: 2.25 }}>
            <StatCard icon={<ConfirmationNumberRounded />} color="#076FF7" value={total} label="Total tokens" />
            <StatCard icon={<CheckCircleRounded />} color="#10B981" value={completed} label="Completed" />
            <StatCard icon={<HourglassTopRounded />} color="#F59E0B" value={pending} label="In queue" />
            <StatCard
              icon={<PaymentsRounded />}
              color="#7C3AED"
              value={formatRupees(data.total_earnings)}
              label="Revenue"
              trend={<>View <ArrowForwardRounded sx={{ fontSize: 14 }} /></>}
              trendColor={colors.primary}
              onClick={() => navigate('/revenue')}
            />
          </Box>

          {/* Breakdown + glance */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 2.25, mt: 3 }}>
            <Box sx={{ ...card, p: 2.5 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>Token status breakdown</Typography>
              {barRows.map((r) => (
                <Box key={r.label} sx={{ my: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: colors.textSecondary }}>{r.label}</span>
                    <span style={{ fontWeight: 700 }}>{r.n}</span>
                  </Box>
                  <Box sx={{ height: 8, borderRadius: 99, bgcolor: colors.gray100, mt: 0.75, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${(r.n / barMax) * 100}%`, bgcolor: r.c, borderRadius: 99, transition: 'width .4s' }} />
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ ...card, p: 2.5 }}>
              <Typography variant="h5" sx={{ mb: 1.5 }}>At a glance</Typography>
              {glance.map((g) => (
                <Box key={g.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                  <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: g.c }} />
                  <Typography sx={{ flex: 1, color: colors.textSecondary }}>{g.label}</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{g.n}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Per-queue breakdown */}
          <Box sx={{ ...card, mt: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5">Per-queue breakdown</Typography>
              <Button size="small" variant="text" endIcon={<ArrowForwardRounded />} onClick={() => navigate('/queues')}>View all</Button>
            </Box>
            {data.queues.length === 0 ? (
              <Box sx={{ px: 2.5, pb: 3 }}><Typography color="text.secondary">No queues for this period.</Typography></Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Queue</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Completed</TableCell>
                    <TableCell align="right">Pending</TableCell>
                    <TableCell align="right">Earnings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.queues.map((q) => (
                    <TableRow key={q.queue_id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/queues/${q.queue_id}`)}>
                      <TableCell sx={{ fontWeight: 600 }}>{q.queue_name}</TableCell>
                      <TableCell align="right">{sumCounts(q.token_counts)}</TableCell>
                      <TableCell align="right">{q.token_counts.completed}</TableCell>
                      <TableCell align="right">{pendingCount(q.token_counts)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: colors.success }}>{formatRupees(q.earnings)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default DashboardPage;
